import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../../store/useStore";
import Loader from "../../components/Loader";
import { formatCurrency, getCartItemGstAmount, getCartTotals } from "../../utils/cartTotals";
import { FaArrowRight, FaCheckCircle, FaLock, FaShoppingCart, FaUndo } from "react-icons/fa";

const getCartItemKey = (item) =>
  `${item.product_id}-${item.product_variant_id ?? "base"}-${item.variant_id ?? "base"}`;

const CartPage = () => {
  const navigate = useNavigate();
  const {
    cart,
    cart_count,
    cart_meta,
    cart_loading,
    cart_error,
    fetchCart,
    incrementItem,
    decrementItem,
    removeFromCart,
    clearCart,
    isAuthenticated,
    openAuthModal,
    is_guest_cart,
    mergeGuestCartOnLogin,
  } = useStore();

  const handleCheckout = () => {
    if (!isAuthenticated) { openAuthModal(); return; }
    navigate("/checkout");
  };

  useEffect(() => {
    if (isAuthenticated && is_guest_cart) {
      mergeGuestCartOnLogin();
    } else {
      fetchCart();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  if (cart_loading && cart.length === 0) {
    return <Loader fullScreen label="Loading cart" sublabel="Syncing your cart items and totals." />;
  }

  if (!cart_loading && cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:py-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-white to-gray-50 p-6 text-center shadow-lg sm:p-10">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary-100 sm:h-24 sm:w-24">
              <svg className="h-10 w-10 text-primary-500 sm:h-12 sm:w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h2 className="mb-2 text-lg font-bold text-gray-900 sm:mb-3 sm:text-2xl">Your cart is empty</h2>
            <p className="mb-6 text-sm text-gray-500 sm:mb-8">Looks like you haven't added anything yet.</p>
            <button
              onClick={() => navigate("/products")}
              className="w-full rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-primary-700 hover:to-primary-800 sm:w-auto sm:px-8"
            >
              Browse Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Use server totals from cart_meta; falls back to local calc for guest cart
  const { subtotal, delivery, gstTotal, total, freeDeliveryMin, amountNeededForFreeDelivery } =
    getCartTotals(cart, cart_meta);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-6 flex items-center justify-between sm:mb-8">
          <div>
            <h1 className="text-xl font-bold text-gray-900 sm:text-3xl">Shopping Cart</h1>
            <p className="mt-1 text-xs text-gray-500 sm:text-sm">({cart_count} items)</p>
          </div>
          <button
            onClick={clearCart}
            className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-red-500 transition-colors hover:bg-red-50 hover:text-red-600 sm:px-3 sm:py-2 sm:text-sm sm:gap-1.5"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span className="hidden sm:inline">Clear Cart</span>
          </button>
        </div>

        {/* Error */}
        {cart_error && (
          <div className="mb-6 flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">
            <svg className="h-4 w-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {cart_error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:gap-8 lg:grid-cols-3">

          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-3 sm:space-y-4">
            {cart.map((item) => (
              <div
                key={getCartItemKey(item)}
                className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm transition-shadow hover:shadow-md sm:p-5 sm:flex-row sm:items-center sm:gap-4"
              >
                {/* Image */}
                <div
                  className="flex h-20 w-20 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 p-2 sm:h-24 sm:w-24"
                  onClick={() => navigate(`/product/${item.product_id}`)}
                >
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="h-full w-full object-contain" />
                  ) : (
                    <span className="text-xs font-semibold uppercase tracking-widest text-gray-300">No Image</span>
                  )}
                </div>

                {/* Info */}
                <div className="min-w-0 flex-1">
                  <h3
                    className="cursor-pointer line-clamp-2 text-sm font-semibold text-gray-900 hover:text-primary-600 sm:text-base sm:line-clamp-1"
                    onClick={() => navigate(`/product/${item.product_id}`)}
                  >
                    {item.name}
                  </h3>
                  {item.variant && (
                    <span className="mt-0.5 inline-block rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                      {item.variant}
                    </span>
                  )}
                  {item.gst > 0 && (
                    <p className="mt-1 text-xs font-semibold text-gray-500">
                      GST {Number(item.gst).toFixed(2)}%
                      {getCartItemGstAmount(item) > 0 ? ` (${formatCurrency(getCartItemGstAmount(item))})` : ""}
                    </p>
                  )}
                  <div className="mt-1.5 flex items-baseline gap-2 sm:mt-2">
                    <span className="text-base font-bold text-primary-600 sm:text-lg">₹{item.price}</span>
                    {item.originalPrice > item.price && (
                      <span className="text-xs text-gray-400 line-through sm:text-sm">₹{item.originalPrice}</span>
                    )}
                  </div>
                </div>

                {/* Qty + Remove */}
                <div className="flex items-center justify-between gap-2 sm:flex-col sm:items-end sm:gap-3">
                  <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gradient-to-r from-gray-50 to-white p-0.5 sm:gap-2 sm:p-1">
                    <button
                      onClick={() => decrementItem(item)}
                      disabled={cart_loading}
                      className="flex h-7 w-7 items-center justify-center rounded-md bg-white text-gray-600 shadow-sm transition-all hover:bg-primary-100 hover:text-primary-600 disabled:opacity-40 sm:h-8 sm:w-8"
                    >
                      <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
                      </svg>
                    </button>
                    <span className="w-6 text-center text-sm font-bold text-gray-900 sm:w-8 sm:text-base">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => incrementItem(item)}
                      disabled={cart_loading}
                      className="flex h-7 w-7 items-center justify-center rounded-md bg-white text-gray-600 shadow-sm transition-all hover:bg-primary-100 hover:text-primary-600 disabled:opacity-40 sm:h-8 sm:w-8"
                    >
                      <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>

                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-sm font-bold text-gray-900 sm:text-base">
                      ₹{(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}
                    </span>
                    <button
                      onClick={() => removeFromCart(item)}
                      disabled={cart_loading}
                      className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-40 sm:p-1.5"
                    >
                      <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={() => navigate("/products")}
              className="flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Continue Shopping
            </button>
          </div>

          {/* Order Summary — server totals */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 rounded-2xl bg-gradient-to-br from-white to-primary-50 p-5 shadow-lg sm:p-6">
              <h2 className="mb-4 text-lg font-bold text-gray-900 sm:mb-6 sm:text-xl">Order Summary</h2>

              <div className="space-y-2 text-xs sm:space-y-3 sm:text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal ({cart_count} items)</span>
                  <span className="font-semibold text-gray-900">₹{subtotal.toFixed(2)}</span>
                </div>
                {gstTotal > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>GST / Tax</span>
                    <span className="font-semibold text-gray-900">₹{gstTotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>Delivery</span>
                  <span className={`font-semibold ${delivery === 0 ? "text-green-600" : "text-gray-900"}`}>
                    {delivery === 0 ? "FREE" : `₹${delivery.toFixed(2)}`}
                  </span>
                </div>
                <div className="my-2 border-t border-dashed border-gray-300" />
                <div className="flex justify-between text-base font-bold text-gray-900 sm:text-lg">
                  <span>Total</span>
                  <span className="text-primary-600">₹{total.toFixed(2)}</span>
                </div>
              </div>

              {/* Free delivery nudge — from server */}
              {amountNeededForFreeDelivery > 0 && (
                <div className="mt-3 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 px-3 py-2 sm:mt-4 sm:px-4 sm:py-3">
                  <p className="text-xs font-medium text-amber-800 sm:text-sm">
                    <FaShoppingCart className="mr-1 inline h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
                    Add <span className="font-bold">Rs {amountNeededForFreeDelivery.toFixed(2)}</span> more for FREE delivery!
                  </p>
                  <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-amber-200 sm:mt-2 sm:h-1.5">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all"
                      style={{ width: `${Math.min((subtotal / freeDeliveryMin) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}

              <button
                onClick={handleCheckout}
                className="mt-4 w-full rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-primary-700 hover:to-primary-800 active:scale-[0.98] sm:mt-6 sm:py-4 sm:text-base"
              >
                <span className="inline-flex items-center justify-center gap-1.5 sm:gap-2">
                  {isAuthenticated ? "Proceed to Checkout" : "Login to Checkout"}
                  {isAuthenticated ? (
                    <FaArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
                  ) : (
                    <FaLock className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
                  )}
                </span>
              </button>

              <div className="mt-3 grid grid-cols-3 gap-1 text-center text-xs text-gray-600 sm:mt-5 sm:gap-2 sm:text-gray-500">
                {[
                  { Icon: FaLock, label: "Secure Pay" },
                  { Icon: FaUndo, label: "Easy Return" },
                  { Icon: FaCheckCircle, label: "Quality" },
                ].map(({ Icon, label }) => (
                  <div key={label} className="rounded-lg bg-white py-1.5 shadow-sm sm:bg-gray-50 sm:py-2">
                    <Icon className="mx-auto h-3.5 w-3.5 text-primary-600 sm:h-4 sm:w-4" aria-hidden="true" />
                    <div className="mt-0.5 text-[10px] font-medium sm:text-xs">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
